import { ReactNode, useEffect, useId, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Link from 'src/components/Link'
import config from 'src/config'

interface Props {
  collapsible: boolean
  pathname: string
  copyright: ReactNode
  footnotes: ReactNode
  children: ReactNode
  backgroundColor?: string
  textColor?: string
  showContact?: boolean
}

/** One disclosure for the complete original footer, with the existing legal strip always visible. */
export default function FooterPresentation({ collapsible, pathname, copyright, footnotes, children, backgroundColor, textColor, showContact }: Props) {
  const [expanded, setExpanded] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  const contentId = useId()
  useEffect(() => {
    if (!expanded) return
    const frame = requestAnimationFrame(() => {
      const element = trigger.current
      if (!element) return
      const header = document.querySelector('header')?.getBoundingClientRect().height || 100
      window.scrollTo({ top: Math.max(0, window.scrollY + element.getBoundingClientRect().top - header - 16), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
    })
    return () => cancelAnimationFrame(frame)
  }, [expanded])
  useEffect(() => { setExpanded(false) }, [pathname])

  if (!collapsible) return <footer style={{ backgroundColor }}>{children}{copyright}{footnotes}</footer>

  return <Box component='footer' data-footer-disclosure='true' sx={{
    backgroundColor: backgroundColor || '#faf8f5', color: textColor || '#17284b',
    borderTop: '1px solid rgba(23, 40, 75, .12)',
    '& a:focus-visible, & button:focus-visible': { outline: '2px solid currentColor', outlineOffset: 3 },
  }}>
    <Container maxWidth='lg' sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: { xs: '0 16px', sm: '0 32px' }, py: 1 }}>
      {copyright}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, fontSize: '.875rem' }}>
        {showContact && ![101, 202].includes(config.shop.shop) && <Link href='/kontakt' style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, color: 'inherit', textUnderlineOffset: '3px' }}>Kontakt</Link>}
        <Box component='button' ref={trigger} type='button' aria-expanded={expanded} aria-controls={contentId}
          onClick={() => setExpanded(value => !value)} sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1, minHeight: 44,
            px: 1, border: 0, borderRadius: '8px', background: 'transparent', color: 'inherit',
            font: 'inherit', fontSize: '.875rem', cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(23, 40, 75, .05)' },
          }}>
          Service & Informationen <span aria-hidden='true'>{expanded ? '−' : '+'}</span>
        </Box>
      </Box>
    </Container>
    <div id={contentId} hidden={!expanded} data-footer-content='true'>
      {children}
      {footnotes}
    </div>
  </Box>
}
